export type LayerAiMode = "quick" | "wizard"

export type LayerAiChatTurn = {
  role: "user" | "assistant"
  /** What the operator sees in the thread. */
  content: string
  /** Fields this assistant turn carried suggestions for. */
  suggestedFields?: string[]
  /**
   * A failed exchange, rendered as an error bubble. Never replayed to the
   * model — it is thread history for the operator, not conversation.
   */
  isError?: boolean
}

/**
 * Carries a running assistant conversation across the fork navigation: the
 * fork flow closes one layer sheet and opens another, unmounting the
 * assistant, so the conversation is stashed here for the fork's own
 * assistant to pick up — the operator continues in the same context instead
 * of starting over.
 */
type LayerAiHandoff = {
  /** The fork's feature code; only its assistant may claim the handoff. */
  targetFeatureCode: string
  mode: LayerAiMode
  turns: LayerAiChatTurn[]
}

let handoff: LayerAiHandoff | null = null

export function setLayerAiHandoff(next: LayerAiHandoff) {
  handoff = next
}

/** Claims (and clears) the stashed conversation for this feature code. */
export function takeLayerAiHandoff(featureCode: string): LayerAiHandoff | null {
  if (handoff?.targetFeatureCode !== featureCode) return null
  const taken = handoff
  handoff = null
  return taken
}

/**
 * Non-consuming check, for the workbench to open the assistant panel before
 * the assistant itself mounts and claims the conversation.
 */
export function hasLayerAiHandoff(featureCode: string): boolean {
  return handoff?.targetFeatureCode === featureCode
}
